export default class ImageEntryModel {
  // system-native props
  entry_id = $state();
  type = $state();
  slug = $state();
  created_at = $state();
  updated_at = $state();
  user_id = $state();
  visibility = $state('public');
  preview_title = $state('');
  preview_preamble = $state('');
  preview_description = $state('');
  preview_images_multiref = $state([]); // not used (just for compat)
  preview_images = $state([]); // not used (just for compat)
  tag_keys = $state([]); // Array of tag keys assigned to this entry
  exclude_from_search = $state(false); // hidden from site search, sitemap and search engines
  // type-specific custom props
  media_type = $state('image');
  src = $state(undefined);
  alt = $state('');
  title = $state('');
  description = $state('');
  preamble = $state('');
  width = $state(undefined);
  height = $state(undefined);
  /** @type {number} value between 0 and 1 */
  focal_point_x = $state();
  /** @type {number} value between 0 and 1 */
  focal_point_y = $state();
  /** @type {boolean} focal point was computed at upload (sharp attention) and not yet touched by a human */
  focal_point_auto = $state(false);
  /** @type {boolean} fit_to_frame was computed at upload (graphic heuristic) and not yet touched by a human */
  fit_auto = $state(false);
  /** @type {Array<{variant_id: string, width: number, height: number, format: string}>} image variants for responsive rendering */
  variants = $state([]);
  /** @type {boolean} wether the image should fit within the boundaries of a container or not */
  fit_to_frame = $state();
  /** @type {number} duration of video in seconds */
  duration = $state();
  /** @type {boolean} whether the image has an audio track */
  audio_track = $state(false);
  /** @type {{x: number, y: number, width: number, height: number}|undefined} crop rectangle in original pixels; `src` is the cut-out */
  crop = $state();
  /** @type {string|undefined} uncropped master, kept so the crop can be widened or undone */
  original_src = $state();
  /** @type {number|undefined} */
  original_width = $state();
  /** @type {number|undefined} */
  original_height = $state();

  // UI (never serialized):
  file_input = $state(undefined);
  /** @type {import('./ImageCropSession.svelte.js').default|null} open crop mode */
  crop_session = $state(null);

  // derive preview from the current entry
  preview = $derived(this.to_preview());

  constructor({
    entry_id,
    type,
    slug,
    created_at,
    updated_at,
    user_id,
    visibility,
    preview_title,
    preview_preamble,
    preview_description,
    tag_keys,
    exclude_from_search,
    media_type,
    src,
    title,
    alt,
    description,
    preamble,
    width,
    height,
    focal_point_x,
    focal_point_y,
    focal_point_auto,
    fit_auto,
    variants,
    fit_to_frame,
    duration,
    audio_track,
    crop,
    original_src,
    original_width,
    original_height,
  } = {}) {
    // system-native props
    this.entry_id = entry_id,
    this.type = type;
    this.slug = slug;
    this.created_at = created_at;
    this.updated_at = updated_at;
    this.user_id = user_id;
    this.visibility = visibility ?? 'public';
    this.preview_title = preview_title ?? '';
    this.preview_preamble = preview_preamble ?? '';
    this.preview_description = preview_description ?? '';
    this.preview_images_multiref = []; // not used - just for compat
    this.preview_images = []; // not used - just for compat
    this.tag_keys = tag_keys ?? [];
    this.exclude_from_search = exclude_from_search ?? false;
    // type-specific custom properties (the source of truth)
    if (media_type) this.media_type = media_type; // defaults to 'image' for backward compatibility
    this.src = src;
    this.title = title ?? '';
    this.alt = alt ?? '';
    this.description = description ?? '';
    this.preamble = preamble ?? '';
    this.width = width ?? undefined;
    this.height = height ?? undefined;
    this.focal_point_x = focal_point_x ?? 0.5;
    this.focal_point_y = focal_point_y ?? 0.5;
    this.focal_point_auto = focal_point_auto ?? false;
    this.fit_auto = fit_auto ?? false;
    this.variants = variants ?? [];
    this.fit_to_frame = fit_to_frame ?? false;
    this.duration = duration ?? undefined;
    this.audio_track = audio_track ?? false;
    this.crop = crop ?? undefined;
    this.original_src = original_src ?? undefined;
    this.original_width = original_width ?? undefined;
    this.original_height = original_height ?? undefined;
    this.file_input = undefined;
  }

  // NOTE: for images we will never show custom preview_images, as that makes no sense
  to_preview() {
    return {
      entry_id: this.entry_id,
      type: this.type,
      slug: this.slug,
      preamble: this.preview_preamble || this.preamble,
      title: this.preview_title || this.title,
      description: this.preview_description || this.description,
      images: [{
        media_type: this.media_type,
        src: this.src,
        alt: this.alt,
        width: this.width,
        height: this.height,
        focal_point_x: this.focal_point_x,
        focal_point_y: this.focal_point_y, 
        variants: this.variants,
        fit_to_frame: this.fit_to_frame,
        duration: this.duration,
        audio_track: this.audio_track,
      }],
      tag_keys: this.tag_keys,
      created_at: this.created_at,
      updated_at: this.updated_at,
      visibility: this.visibility,
      // lets a member's collection editor offer caption fields for their own pictures only
      user_id: this.user_id,
    }
  }

  // serialized version of an entry
  to_json() {
    return {
      entry_id: this.entry_id,
      type: this.type,
      visibility: this.visibility,
      preview_title: this.preview_title,
      preview_preamble: this.preview_preamble,
      preview_description: this.preview_description,
      preview_images_multiref: this.preview_images_multiref,
      tag_keys: this.tag_keys,
      exclude_from_search: this.exclude_from_search || undefined,
      media_type: this.media_type,
      src: this.src,
      title: this.title,
      alt: this.alt,
      description: this.description,
      preamble: this.preamble,
      width: this.width,
      height: this.height,
      focal_point_x: this.focal_point_x,
      focal_point_y: this.focal_point_y,
      focal_point_auto: this.focal_point_auto,
      fit_auto: this.fit_auto,
      variants: this.variants,
      fit_to_frame: this.fit_to_frame,
      duration: this.duration,
      audio_track: this.audio_track,
      crop: this.crop,
      original_src: this.original_src,
      original_width: this.original_width,
      original_height: this.original_height,
    };
  }

  toJSON() {
    return this.to_json();
  }
}


// OTHER PROPS TO BE IMPLEMENTED LATER:

/**
 * `embedded_in_entries`: Array<Entry Preview>
 * An array of previews of entries that have this image embedded.
 */

/**
 * `moderation_flags`: Array<String>
 * An array of strings that serve as content warnings or moderation flags for the image.
 * Each string represents a specific type of content that may require user discretion or moderation,
 * such as "nudity" or "spoiler". This is useful for content filtering and user safety measures.
 */

/**
 * `palette`: Array<String>
 * An array of hexadecimal color values extracted from the image. This feature can be used
 * for color analysis, creating matching color schemes, or for aesthetic purposes in UI design.
 */

/**
 * `src_webp` | `src_avif` | `src_jpeg`: String
 * The URL pointing to the specific format of the image.
 */

/**
 * `copyright`: String
 * Contains information about the copyright status of the image. This includes details
 * about the owner of the copyright and any related legal considerations or restrictions
 * on the image's use.
 */

/**
 * `author`: String
 * The name or identifier of the individual or entity that created or authored the image.
 * This is important for attribution, copyright, and for understanding the source of the image.
 */

/**
 * `exif_info`: Object
 * This property would store the EXIF data associated with the image (e.g., camera settings, date,
 * location), which can be useful for sorting, filtering, or understanding the context of the image.
 */

/**
 * `location`: Object
 * An object containing the geographical coordinates (latitude and longitude) where the image
 * was taken.
 */

/**
 * `original_filename`: String
 * The original filename of the image as it was uploaded or created.
 */

/**
 * `md5` | `sha1_hash`: String
 * A string representing the MD5 or SHA1 hash of the image file. Hashing is used to ensure
 * file integrity, allowing for verification that the image has not been altered or corrupted.
 * It is also useful for identifying duplicate files.
 */

/**
 * `annotations`: Array<{label: string, x: number, y: number}>
 * Textual annotations layered ontop of the image to mark specific features or segments of the image
 */